/**
 * Redigera ett kundprojekt på plats – samma fönster på leadsidan, på
 * kundkortet och i kundprojektvyn. Alla fält: grunddata, säkerhet, parter
 * och (när det öppnas från ett lead) testkörningen för det leadet.
 *
 * Sparar bara de fält som ändrats, ovanpå den aktuella versionen
 * (senaste ändring per fält vinner), och synkar de avledda fälten i samma
 * transaktion. Om någon annan sparar medan fönstret är öppet visas en
 * varning direkt.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Alert, Divider, Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { valideraKundprojekt, KP_BYGGSKEDEN, KP_STATUSAR, andradeFalt } from './kundprojekt';
import { sparaKundprojektAndringar, bevakaKundprojekt } from './kundprojektData';
import { toMillis } from './pipelineFlode';
import {
  NamnFalt, GrunddataFalt, SakerhetFalt, ParterFalt, TestkorningFalt, Val, FaltRubrik, useKundKontakter,
} from './KundprojektFalt';

/** "Sparat" som kort bekräftelse. [element, visa(text, varning?)] */
export function useSparatKvitto() {
  const [kvitto, setKvitto] = useState(null);
  const element = (
    <Snackbar open={!!kvitto} autoHideDuration={kvitto?.varning ? 8000 : 2500} onClose={() => setKvitto(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
      <Alert severity={kvitto?.varning ? 'warning' : 'success'} variant="filled" onClose={() => setKvitto(null)}>
        {kvitto?.text}
      </Alert>
    </Snackbar>
  );
  return [element, (text, varning = false) => setKvitto({ text, varning })];
}

export const KONFLIKT_TEXT = 'Kundprojektet hade ändrats av någon annan sedan du öppnade det. Dina ändrade fält sparades; övriga ändringar behölls.';

/**
 * @param {object} p
 *   kundprojekt  versionen som öppnas (med id och uppdaterad)
 *   leadId       leadet man står på – visar testkörningen för det
 *   companies    alla kunder (läses in om de saknas)
 *   uid, onClose, onSparad(resultat)
 */
export default function KundprojektRedigering({ kundprojekt, leadId, companies: givnaKunder, uid, onClose, onSparad }) {
  const oppnad = useRef(kundprojekt);
  const senastKand = useRef(toMillis(kundprojekt.uppdaterad));
  const sparar = useRef(false);
  const [kp, setKp] = useState(() => ({ testkorningar: {}, ...kundprojekt }));
  const [companies, setCompanies] = useState(givnaKunder || []);
  const [andradAvAnnan, setAndradAvAnnan] = useState(false);
  const [raderad, setRaderad] = useState(false);
  const [sparaPagar, setSparaPagar] = useState(false);
  const [fel, setFel] = useState('');
  const [kontakterFor, laddaKontakter] = useKundKontakter();

  useEffect(() => {
    if (givnaKunder?.length) return;
    getDocs(query(collection(db, 'crmCompanies'), orderBy('name')))
      .then(s => setCompanies(s.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
  }, [givnaKunder]);

  // Samtidig redigering: varna när dokumentet sparas av någon annan.
  useEffect(() => bevakaKundprojekt(kundprojekt.id, aktuell => {
    if (!aktuell) { setRaderad(true); return; }
    const t = toMillis(aktuell.uppdaterad);
    if (t != null && t !== senastKand.current && !sparar.current) setAndradAvAnnan(true);
  }), [kundprojekt.id]);

  const satt = (falt, varde) => setKp(k => ({ ...k, [falt]: varde }));
  const andrade = andradeFalt(oppnad.current, kp);

  const testkontakter = useMemo(() => {
    const kundNamn = new Map(companies.map(c => [c.id, c.name]));
    return kp.parter.flatMap(p => (kontakterFor(p.kundId) || []).map(k => ({ ...k, kund: kundNamn.get(p.kundId) })));
  }, [kp.parter, companies, kontakterFor]);
  const leadKopplat = !!leadId && kp.parter.some(p => (p.leadIds || []).includes(leadId));

  async function spara() {
    const valideringsfel = valideraKundprojekt(kp);
    if (valideringsfel.length) { setFel(valideringsfel.join(' ')); return; }
    sparar.current = true;
    setSparaPagar(true);
    setFel('');
    try {
      const resultat = await sparaKundprojektAndringar(oppnad.current, kp, uid);
      onSparad?.(resultat);
      onClose();
    } catch (e) {
      console.error('Kunde inte spara kundprojektet', e);
      setFel(e.message || String(e));
      sparar.current = false;
    } finally {
      setSparaPagar(false);
    }
  }

  return (
    <Dialog open onClose={sparaPagar ? undefined : onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <EditIcon sx={{ color: '#FF6B00' }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight="bold" noWrap>Redigera kundprojekt</Typography>
          <Typography variant="caption" color="text.secondary">{kundprojekt.namn}</Typography>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {raderad && <Alert severity="error">Kundprojektet har raderats av någon annan. Det går inte att spara.</Alert>}
          {andradAvAnnan && !raderad && (
            <Alert severity="warning">
              Någon annan har sparat ändringar i kundprojektet sedan du öppnade det. Sparar du skrivs bara de
              fält du själv ändrat – övriga ändringar behålls.
            </Alert>
          )}
          <NamnFalt kp={kp} satt={satt} />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Val label="Byggskede" lista={KP_BYGGSKEDEN} value={kp.byggskede} onChange={x => satt('byggskede', x)} />
            <Val label="Status" lista={KP_STATUSAR} value={kp.status} onChange={x => satt('status', x)} />
          </Box>
          <GrunddataFalt kp={kp} satt={satt} medStatus={false} />
          <FaltRubrik>Säkerhet</FaltRubrik>
          <SakerhetFalt kp={kp} satt={satt} full />
          <ParterFalt kp={kp} setKp={setKp} companies={companies}
            kontakterFor={kontakterFor} laddaKontakter={laddaKontakter} />
          {leadKopplat && (
            <TestkorningFalt kontakter={testkontakter} varde={kp.testkorningar?.[leadId]}
              onChange={t => setKp(k => ({ ...k, testkorningar: { ...(k.testkorningar || {}), [leadId]: t } }))} />
          )}
          {fel && <Alert severity="error">{fel}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
          {andrade.length ? `${andrade.length} ändrade fält` : 'Inga ändringar'}
        </Typography>
        <Button onClick={onClose} disabled={sparaPagar} sx={{ color: 'text.secondary' }}>Avbryt</Button>
        <Button variant="contained" onClick={spara} disabled={sparaPagar || raderad || !andrade.length || !kp.namn.trim()}
          sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' }, fontWeight: 700 }}>
          {sparaPagar ? 'Sparar…' : 'Spara'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
